/**
 * OFC (Order Fulfillment Control) sanctioned vocabulary — SINGLE SOURCE of the
 * bilingual pairs every business-facing surface must use.
 *
 * Rules (holistic plan, Fixed decisions):
 * - Business-facing EN = OFC terms: Fulfillment Node / Quantity Buckets /
 *   Order Balance / Shipment Batch.
 * - Business-facing zh = the Flow-F plain-label set: 進度點 / 數量分段 /
 *   待出量·未出量 / 裝櫃批次 / 訂單異常.
 * - 履約 NEVER appears in zh user copy. 倉庫 / 庫存 / 在庫 / 儲位 / 盤點 (and EN
 *   Warehouse / Inventory) are NEVER business-facing anywhere.
 * - Product 產品 is business-facing; Item 品項 only for NetSuite/DB/API surfaces.
 *
 * Enforcement: `scripts/vocab-lint.cjs` (wired into `npm run lint`) parses the
 * BANNED_* and SANCTIONED_ALTERNATIVES exports below — keep them
 * machine-readable (flat arrays of single-quoted string literals).
 *
 * THIS FILE IS EXEMPT from the lint's own scan (`EXCLUDED_FILES` in
 * vocab-lint.cjs, plus the `__tests__` directory exclusion for its test). That
 * exemption is what lets the deny-list definitions and the "drawn but banned"
 * notes below name the banned terms without the gate firing on its own source
 * of truth. Do not move a banned term out of this file into a scanned one.
 */

export interface OfcTerm {
  en: string;
  zh: string;
  /** business = user copy; technical = NetSuite/DB/API surfaces only */
  scope: 'business' | 'technical';
  note?: string;
}

export const OFC_TERMS = {
  orderControlCenter: {
    en: 'Order Control Center',
    zh: '訂單控管中心',
    scope: 'business',
    note: 'OFC/M1 — ONE module; derive-never-restore.',
  },
  fulfillmentNode: {
    en: 'Fulfillment Node',
    zh: '進度點',
    scope: 'business',
  },
  quantityBuckets: {
    en: 'Quantity Buckets',
    zh: '數量分段',
    scope: 'business',
  },
  orderBalance: {
    en: 'Order Balance',
    zh: '待出量',
    scope: 'business',
    note: '未出量 also sanctioned; derived qtyOrdered − qtyLoaded, never stored. NEVER 待履約量.',
  },
  shipmentBatch: {
    en: 'Shipment Batch',
    zh: '裝櫃批次',
    scope: 'business',
  },
  orderException: {
    en: 'Order Exception',
    zh: '訂單異常',
    scope: 'business',
  },
  shippingNotice: {
    en: 'Shipping Notice',
    zh: '出貨通知',
    scope: 'business',
  },
  goodsReceipt: {
    en: 'Goods Receipt',
    zh: '進貨',
    scope: 'business',
    note: 'Lives under 採購 Procurement (Board E pink placement) — never an Inventory section.',
  },
  availableQuantity: {
    en: 'Available Quantity',
    zh: '可供量',
    scope: 'business',
    note: 'NetSuite availability read-through label — never 庫存.',
  },
  reorderSuggestion: {
    en: 'Reorder Suggestion',
    zh: '補貨建議',
    scope: 'business',
    note: 'v1 computed ROP suggestion surfaced as a review queue feeding the demand pool — never an auto-PO.',
  },
  onOrderQuantity: {
    en: 'On-Order Quantity',
    zh: '在途量',
    scope: 'business',
    note: 'Open-PO quantity open to receive (Tier-2 derived) — never 在途庫存 / 庫存.',
  },
  targetLevel: {
    en: 'Target Level',
    zh: '目標水位',
    scope: 'business',
    note: 'Preferred Stock Level relabelled off the 庫存 glyph (OQ-4). Dictionary name 偏好庫存水位 lives only in registry netSuiteField.',
  },
  projectedAvailable: {
    en: 'Projected Available',
    zh: '預計可供量',
    scope: 'business',
    note: 'Running-balance projection (PAB analog) label — never 庫存.',
  },
  product: {
    en: 'Product',
    zh: '產品',
    scope: 'business',
    note: 'Primary identity = Internal Product ID (system-assigned).',
  },
  item: {
    en: 'Item',
    zh: '品項',
    scope: 'technical',
    note: 'NetSuite/DB/API surfaces ONLY — never business-facing copy.',
  },
  purchasingSupply: {
    en: 'Purchasing & Supply',
    zh: '採購與供應',
    scope: 'business',
    note: 'Product-detail tab label. The drawn 採購與庫存 cannot ship (banned term). Q-M1 option pair: (a) rename — SHIPPED as build-default; (b) amend BANNED_BUSINESS_TERMS — NOT taken.',
  },
  onHand: {
    en: 'On Hand',
    zh: '現有量',
    scope: 'business',
    note: 'DD!75 Quantity On Hand read-through column label — sanctioned relabel of the drawn 在庫數量, ItemLocationsSection precedent (各地點可供量).',
  },
  onHandBaseUnit: {
    en: 'On Hand (Base Unit)',
    zh: '基本單位現有量',
    scope: 'business',
    note: 'DD!76 Quantity On Hand (Base Unit) read-through column label.',
  },
  bookValue: {
    en: 'Value',
    zh: '帳面價值',
    scope: 'business',
    note: 'DD!77 Value read-through column label — the drawn zh 庫存價值 contains a banned term and NEVER ships.',
  },
  averageCost: {
    en: 'Average Cost',
    zh: '平均成本',
    scope: 'business',
    note: 'DD!78 Average Cost read-through column label (NetSuite-computed, display only).',
  },
  landedCost: {
    en: 'Landed Cost',
    zh: '到岸成本',
    scope: 'business',
    note: 'Pricing waterfall stage-2 canonical label — ONE label per stage. The alias "True Inventory Cost" (banned term) and the full 編號A/編號B alias sets live ONLY in docs/pricing-aliases.md, never in src.',
  },
} as const satisfies Record<string, OfcTerm>;

export type OfcTermKey = keyof typeof OFC_TERMS;

/**
 * Vendor confirmation checkpoints (SO→PO board).
 * UI copy ALWAYS renders these as bilingual labels (e.g. 「A1 估計」);
 * bare A-codes are allowed in code/tests only.
 */
export type VendorCheckpointCode = 'A1' | 'A2' | 'A3';

export const VENDOR_CHECKPOINTS: Record<
  VendorCheckpointCode,
  { code: VendorCheckpointCode; en: string; zh: string }
> = {
  A1: { code: 'A1', en: 'Estimate', zh: '估計' },
  A2: { code: 'A2', en: 'Shipping Notice Re-confirm', zh: '出貨通知再確認' },
  A3: { code: 'A3', en: 'Actual at Loading', zh: '裝櫃實際' },
};

/** Render a checkpoint as its sanctioned bilingual label, e.g. "A1 估計". */
export function formatVendorCheckpoint(code: VendorCheckpointCode, lang: 'en' | 'zh'): string {
  const cp = VENDOR_CHECKPOINTS[code];
  return `${cp.code} ${lang === 'zh' ? cp.zh : cp.en}`;
}

/**
 * Deny-list: never business-facing, anywhere (EN or zh).
 * Parsed by scripts/vocab-lint.cjs — keep as a flat array of string literals.
 *
 * 在庫 / 盤點 added 2026-08-21: the REFRAME bans 在庫量 and 在庫數量 as
 * business-facing, but NEITHER contains the substring 庫存, so both passed the
 * gate (a latent hole, not a live violation — they appear nowhere in shipped
 * copy). The 在庫 stem subsumes both. 盤點 (stocktake) is the same shape: a
 * physical-count concept with no surface in a direct-ship MVP.
 *
 * NOT added, and why (each probed against this lint — see the commit body):
 * - 調撥 — fires on 5 legitimate existing sites, incl. the shipped zh label
 *   `itemLocations.columns.inTransit` = 在途調撥, a column HELD pending OQ-18.
 *   Banning it is a vocabulary ruling on OQ-18, not a gate-hole close.
 * - 待履約量 — already rejected by 履約 in BANNED_ZH_USER_COPY (substring). A
 *   second entry could never fail on its own, so it would not be a guard.
 * - Fulfillment Node — not latent: it is OFC_TERMS.fulfillmentNode.en and ships
 *   in 4 en/occ.json values. Retiring it is a relabel wave, not a lint change.
 * - Bin — clean today, but the EN matcher is a PREFIX match (`\bBin`, no
 *   trailing boundary), so any future "binding" / "binary" / "binder" in user
 *   copy would fail the build citing warehouse vocabulary. Its zh half 儲位 is
 *   already banned. Adding it safely needs a matcher change that would narrow
 *   the existing Inventory/Warehouse prefix match (Inventories, Warehouses).
 */
export const BANNED_BUSINESS_TERMS = [
  '庫存',
  '在庫',
  '倉庫',
  '儲位',
  '盤點',
  'Inventory',
  'Warehouse',
] as const;

/**
 * Deny-list: never in zh user copy (use the Flow-F plain labels instead).
 * Parsed by scripts/vocab-lint.cjs — keep as a flat array of string literals.
 */
export const BANNED_ZH_USER_COPY = ['履約'] as const;

/**
 * The sanctioned word to use INSTEAD of each banned term, so vocab-lint's
 * failure message names the replacement rather than making the author go
 * looking for it.
 *
 * Format: '<banned term> → <sanctioned use>' — one flat array of single-quoted
 * literals, read by the same parser as the deny lists above, so there is one
 * shape and nothing to keep in sync. Every BANNED_BUSINESS_TERMS and
 * BANNED_ZH_USER_COPY entry MUST have exactly one row here; vocab-lint exits 1
 * when one is missing (and ofcVocabularyPairs.test.ts pins the coverage).
 *
 * BANNED_TAXONOMY_TERMS is deliberately NOT covered: that list is file-scoped
 * graph-theory vocabulary whose replacement is always plain 分類 / 子分類 /
 * 所屬分類, already stated in its own doc block.
 *
 * A replacement may never itself contain a banned term (pinned by
 * ofcVocabularyPairs.test.ts) — so these rows name the sanctioned word only and
 * never restate the banned form they replace.
 */
export const SANCTIONED_ALTERNATIVES = [
  '庫存 → 現有量 / 目標水位 / 安全備貨天數（依語意選用）',
  '在庫 → 現有量（On Hand）',
  '倉庫 → 裝櫃點',
  '儲位 → 裝櫃點',
  '盤點 → 各階段數量 / 裝櫃批次',
  'Inventory → On Hand 現有量 / Target Level 目標水位（NetSuite 欄位名維持英文）',
  'Warehouse → Loading Point 裝櫃點',
  '履約 → 訂單控管中心 / 待出量·未出量',
] as const;

/**
 * Deny-list: graph-theory taxonomy vocabulary that must never surface in the
 * 5-level product-category copy. Business users see plain 分類 / 子分類 /
 * 所屬分類 language — never parent/child/node/leaf/root graph terms.
 *
 * SCOPE (critical): scripts/vocab-lint.cjs applies these ONLY to the category
 * locale files (categories.json / taxonomy.json / products.json), NOT globally.
 * This is deliberate belt-and-suspenders:
 * - Banned entries are graph-theory PHRASES + CJK, never the bare English
 *   words "parent" / "child" — so legitimate copy like "Parent Company" and
 *   source identifiers such as parentId / childLevel / node stay untouched.
 * - The file-scope keeps 父公司 (customers.json) and every source `node`
 *   identifier legal even though the CJK/word forms appear here.
 * Do NOT merge these into BANNED_BUSINESS_TERMS (that list is scanned across
 * every locale + all src string literals and would nuke the legitimate uses).
 * Parsed by scripts/vocab-lint.cjs — keep as a flat array of string literals.
 */
export const BANNED_TAXONOMY_TERMS = [
  'parent category',
  'child category',
  'node',
  'leaf node',
  'root node',
  '父節點',
  '子節點',
  '根節點',
  '葉節點',
  '節點',
  '父類別',
  '子類別',
] as const;

/**
 * D8 "Item" ratchet baseline (W-1/R12 Fix7) — the frozen set of EN locale
 * strings that may still contain a standalone "Item".
 *
 * WHY A BASELINE AND NOT A BAN. `Item` cannot join BANNED_BUSINESS_TERMS: that
 * list is zero-tolerance, and the D8 policy deliberately KEEPS the
 * NetSuite-literal field names ("Item Defined Cost", "NetSuite item ID") and
 * the build-up row label. A zero-tolerance ban would red six legitimate
 * strings on the first run and be deleted the same afternoon. A frozen
 * baseline is the estate's own `ui-text-literals` / `check-raw-hex` shape:
 * the count may only go DOWN.
 *
 * THE MATCHER IS SHIPPED BESIDE THE NUMBER so the next person can re-measure
 * instead of trusting this comment:
 *
 *     matcher   /\bItem\b/   — case-SENSITIVE, word-bounded, EN locales only
 *     scope     src/i18n/locales/en/ **, every string value, all namespaces
 *     re-measure   node scripts/vocab-lint.cjs --d8-list
 *
 * Case sensitivity is load-bearing and was got wrong once already: the wave's
 * own draft baseline listed `fields.netSuiteItemId` = "NetSuite item ID",
 * which this matcher can NEVER see (lowercase `item`). A baseline naming a
 * string the matcher cannot match is a green floor that cannot pass. Every
 * entry below was produced BY the matcher, not by reading the files.
 *
 * RE-BASELINE PROCEDURE (the part that stops this gate being deleted the first
 * time it is inconvenient):
 *  1. The count may only go DOWN. When a string is fixed, run
 *     `node scripts/vocab-lint.cjs --d8-list`, paste the re-derived array body
 *     it prints, and land it in the SAME commit as the fix. The lint tells you
 *     when the baseline is stale; a stale entry is a message, never a failure,
 *     so removing an "Item" string can never red the build.
 *  2. An INCREASE requires the reason in the same commit body — a new EN
 *     string carrying "Item" is either a NetSuite/DB literal (say which field)
 *     or a D8 violation (fix it instead). There is no third case.
 *  3. Re-derive at each landing. This array is measured at develop `24171e1`
 *     with Fix6 applied: 6 entries (7 before Fix6 removed
 *     `pricing.tiers.specialOverride` = "Special Item Override").
 *
 * Format: '<locale basename> | <dotted key path> | <exact EN value>'.
 * Read by scripts/vocab-lint.cjs with the same flat-array parser as the deny
 * lists above — keep it a flat array of single-quoted string literals, and do
 * NOT fold these into BANNED_BUSINESS_TERMS.
 */
export const D8_ITEM_BASELINE = [
  'customers.json | customer.notifications.cannotDeactivateDefault | Cannot Deactivate Default Item',
  'pricing.json | pricing.panel.waterfall.item | Item',
  'pricing.json | pricing.costEstimateType.item_defined_cost | Item Defined Cost',
  'products.json | fields.itemDefinedCost | Item Defined Cost',
  'products.json | fields.itemDefinedCostUnit | Item Defined Cost Unit',
  'products.json | detail.trial.noDefinedCost | Enter an Item Defined Cost above to start the trial',
] as const;
